<template>
  <article class="article px-4 sm:px-0 sm:pt-7.5" :class="{ quiz: isQuiz }" :data-legacy-id="article.legacyId" :data-id="article._id">
    <div ref="articleVisibleStartRef"></div>
    <ArticleContentType v-if="isSpecialContent" type="special" />
    <ArticleContentType v-if="isExpertContent" type="expert" />
    <ArticleBreadcrumb v-if="showArticleHeader && !hide.includes('breadcrumb')" :stn="article.isStn" />
    <ArticleTitle v-if="showArticleHeader && !hide.includes('title')" :stn="article.isStn" />
    <ArticleTags v-if="article.hashtags.length && (showArticleHeader || isMobile) && !hide.includes('tags')" :stn="article.isStn" />
    <section
      v-if="!isMobile && showArticleHeader && !hide.includes('meta')"
      class="meta flex flex-row items-center justify-between pt-2 pb-1.5"
    >
      <ArticleAuthor v-if="!hide.includes('author')" :stn="article.isStn">
        <ArticleDate slot="article_date" :stn="article.isStn" class="font-normal text-sm text-primary-200" />
      </ArticleAuthor>
      <ArticleStats
        v-if="hasFlag('showSharesCount') && !hide.includes('shareCount')"
        :count="article.stats.shares"
        :text="$t('news-page.share-stat')"
        class="px-2"
      />
      <ArticleStats
        v-if="hasFlag('showViewCount') && !hide.includes('viewCount')"
        :count="article.stats.views"
        :text="isVideo ? $t('news-page.watch-stat') : $t('news-page.read-stat')"
        class="px-4"
      />
    </section>
    <div v-if="isMobile && !hide.includes('author')" class="pt-2">
      <ArticleAuthor class="flex" />
      <ArticleDate v-if="isMobile" class="mt-4" />
      <ArticleShareMobileTop v-if="!isAppView" />
    </div>
    <!-- INFO: bu kısım test amaçlı kapatıldı, impression _slug içerisinde gönderiliyor -->
    <ArticleImpression @on-screen="$emit('stn-impression', article, isLoggedIn)" />
    <template v-for="(entry, i) in entries">
      <OEntry v-if="!hide.includes('entries')" :key="entry._id" :entry="entry" />
      <TableOfContent v-if="isMobile && i === 0 && isTocActive" :key="'toc_' + entry._id" class="bg-[#f7f8fb] mb-4" />
      <ArticleVideoAds
        v-if="i === 1 && !onlyMasthead"
        :key="entry._id + '_videoAds'"
        :entries-number="validAdEntries.length"
        :entry-number="i"
      />
      <AdController v-if="i === lastEntryBeforeOioEmbed" :key="entry._id + '_bottomBranding'" unit="bottomBranding" />
      <ServerOnly v-if="i === lastEntryBeforeOioEmbed" :key="'abreco_' + i">
        <div id="abtest-reco-div-1"></div>
      </ServerOnly>
      <ArticleAds
        :key="entry._id + '_ad'"
        :entries="validAdEntries"
        :entry="entry"
        :stn-page="stnPage"
        :is-stn-active="article.isStnActive"
      />
      <client-only :key="entry._id + '_read'">
        <div v-if="articleReadEndIndex === i">
          <ArticleReadEnd @on-screen="handleArticleReadEnd" />
        </div>
      </client-only>
      <KesfetRedirectButton v-if="i === lastEntryBeforeOioEmbed" :key="i + '_kesfetRedirect'" class="my-4" />
    </template>

    <div ref="articleContentReadRef"></div>
    <!-- kunye -->
    <ArticleKunye v-if="!hide.includes('kunye')" />
    <!-- article share -->
    <RecommendationWidget v-if="IS_ONEDIO && isMobile" :article="item" :count="4" class="my-6 -mx-4" />
    <ArticleAds v-if="isMobile" unit-name="icerikArasiBottom" />
    <ArticleAds v-if="isMobile" unit-name="icerikSonuDikey" />
    <ArticleShareMobile v-if="isMobile" class="flex sm:hidden" />
    <AdController unit="icerikSonuSosyalMedya" />
    <div ref="articleVisibleEndRef"></div>
  </article>
</template>

<script>
import { quizResult } from '@/modules/quiz/utils/initQuiz';
import { computed, defineComponent, provide, ref, inject, onMounted } from '@nuxtjs/composition-api';
import ArticleBadge from '@/modules/common/components/article/ArticleBadge';
import AdRenderer from '@/modules/ads/components/AdRenderer';
import AdController from '@/modules/ads/components/AdController';
import { initializeAdLoaders } from '@/modules/ads/adUtils';
import { useTracking } from '@/modules/tracking/composables/useTracking';
import { sendContentReadEvent } from '@/modules/tracking/scripts/dataLayer';
import { cloneDeep, findLastIndex } from 'lodash';
import RecommendationWidget from '@/modules/article/components/RecommendationWidget';
import { changeHistory, hasQuestion, isVideoArticle } from '../utils/articleUtils';
import ArticleVideoAds from './ArticleVideoAds';
import ArticleStats from './ArticleStats';
import ArticleAuthor from './ArticleAuthor';
import ArticleKunye from './ArticleKunye';
import ArticleDate from './ArticleDate';
import ArticleAds from './ArticleAds';
import ArticleShareMobileTop from './ArticleShareMobileTop';
import ArticleShareMobile from './ArticleShareMobile';
import ArticleBreadcrumb from './ArticleBreadcrumb';
import ArticleContentType from './ArticleContentType';
import ArticleTags from './ArticleTags';
import ArticleTitle from './ArticleTitle';
import OEntry from './OEntry';
import { abbreviateNumber } from '~/modules/common/utils/stringUtils';
import { useDevice } from '~/composables/useDevice';
import { useInfiniteScroll } from '~/composables/useInfiniteScroll';
import ArticleImpression from '~/modules/article/components/ArticleImpression';
import { useAuth } from '~/modules/auth/state/useAuth';
import ArticleReadEnd from '~/modules/article/components/ArticleReadEnd';
import { IS_ONEDIO } from '~/constants/config';
import { useWebSocket } from '~/composables/useWebSocket';
import { ServerOnly } from '~/modules/common/components/ui';
import KesfetRedirectButton from '~/modules/kesfet/components/KesfetRedirectButton.vue';
import TableOfContent from '~/modules/article/components/TableOfContent.vue';
import { isAstrolojiContent } from '~/modules/astrology/utils/astrologyUtils';

export default defineComponent({
  props: {
    item: {
      type: Object,
      required: true,
    },
    stnPage: {
      type: Number,
      required: false,
    },
    isAppView: {
      type: Boolean,
      required: false,
    },
    hide: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  components: {
    ArticleReadEnd,
    ArticleImpression,
    ArticleBadge,
    ArticleDate,
    ArticleAuthor,
    ArticleKunye,
    ArticleStats,
    ArticleShareMobileTop,
    ArticleShareMobile,
    ArticleBreadcrumb,
    ArticleContentType,
    ArticleTags,
    AdRenderer,
    ArticleAds,
    AdController,
    OEntry,
    RecommendationWidget,
    ArticleTitle,
    ArticleVideoAds,
    ServerOnly,
    KesfetRedirectButton,
    TableOfContent,
  },
  head: {},
  setup(props, { emit }) {
    const { isMobile } = useDevice();
    const { isLoggedIn } = useAuth();
    const { BindMeta } = useWebSocket();
    // This part is need to create ad config for each new article, like at STN
    const adConfig = props.item.isStn ? ref({}) : inject('adConfig');
    const isOnedioVideo = computed(() => isVideoArticle(props.item));
    const showArticleHeader = computed(() => (!isOnedioVideo.value && !props.item.isStn) || props.item.isStn);
    const articleRef = ref(props.item);
    const { initiliazeArticleEvents } = useTracking();
    if (props.item.isStn) {
      const { serversideDataGenerator, generateSponsoredArticles } = initializeAdLoaders();
      const adConfigNew = serversideDataGenerator(articleRef, adConfig);
      onMounted(async () => {
        await generateSponsoredArticles(adConfig);
      });
      provide('adConfig', adConfigNew);
    }
    initiliazeArticleEvents(articleRef);

    const hasBadge = computed(() => props.item?.badges?.length);
    const entries = computed(() =>
      props.item.entries.filter((entry) => {
        if (entry.metadata?.hideIn?.desktop && !isMobile.value) {
          return false;
        } else if (entry.metadata?.hideIn?.mobile && isMobile.value) {
          return false;
        }
        return true;
      })
    );

    const lastEntryBeforeOioEmbed = computed(() => findLastIndex(entries.value, (entry) => entry.mode !== 'oio-embed'));

    const [articleVisibleStartRef] = useInfiniteScroll(
      () => {
        if (props.isAppView) return;
        if (isAstrolojiContent) return;
        if (document.location.pathname === props.item.route) return;
        changeHistory(props.item);
        if (props.item.isStn) {
          BindMeta(); // websocket BindMeta
        }
      },
      {
        rootMargin: '0% 0% -50% 0%',
      }
    );

    const [articleVisibleEndRef] = useInfiniteScroll(() => {
      if (props.isAppView) return;
      if (isAstrolojiContent) return;
      if (document.location.pathname === props.item.route) return;
      changeHistory(props.item);
      BindMeta(); // websocket BindMeta
    });

    const articleContentReadRef = sendContentReadEvent(props.item, adConfig);
    // article read end event
    const handleArticleReadEnd = () => {
      if (props.isAppView) return;
      emit('oet-impression', 'item_read_complete', articleRef, props.item.isStn);
    };
    // finds last index before oio-embed
    const articleReadEndIndex = ref(null);
    if (process.client) {
      const e = cloneDeep(entries.value).reverse();
      const index = e.findIndex((entry) => entry.mode !== 'oio-embed');
      articleReadEndIndex.value = e.length - index - 1;
    }
    const isVideo = computed(() => props.item?.flags?.isVideo || false);
    const isQuiz = computed(() => hasQuestion(props.item));
    const validAdEntries = computed(() =>
      entries.value.filter(
        (entry, index) => !entry?.metadata?.hasOwnProperty?.('question_result') && entries.value?.[index + 1]?.mode !== 'oio-embed'
      )
    );

    const isSpecialContent = computed(() => props.item?.tags?.includes('~special-content') || false);
    const isExpertContent = computed(() => props.item?.tags?.includes('~expert-content') || false);

    const hasFlag = (flag) => props.item.flags?.[flag] || false;

    const isTocActive = computed(() => props.item.flags?.showTableOfContents);

    provide('article', props.item);
    return {
      isMobile,

      // article
      article: props.item,
      entries,
      hasBadge,
      isVideo,
      isOnedioVideo,
      showArticleHeader,
      onlyMasthead: adConfig.value?.onlyMasthead,

      // quiz
      isQuiz,
      quizResult,

      // utils
      hasFlag,
      abbreviateNumber,
      validAdEntries,

      articleVisibleStartRef,
      articleVisibleEndRef,
      articleContentReadRef,

      // article read case
      articleReadEndIndex,
      handleArticleReadEnd,

      isLoggedIn,
      lastEntryBeforeOioEmbed,

      isSpecialContent,
      isExpertContent,
      IS_ONEDIO,
      isTocActive,
    };
  },
});
</script>

<style lang="scss">
.btn--google-news {
  img {
    width: 140px;
    height: 35px;
  }
}

.article {
  h2 a, // entry title'ların içindeki link test sonu örnek: 967901
  .entry--text div a,
  .entry--image figcaption a,
  .entry--video figcaption a,
  a.external-link {
    color: var(--text-anchor);
    &:hover {
      color: var(--hover-text-anchor);
    }
  }
  .entry {
    -webkit-font-smoothing: antialiased;
  }

  .entry figure figcaption ul {
    margin: 7px 0 15px;
    padding-left: 30px;
    list-style: disc;
    li {
      line-height: 22px;
      position: relative;
    }
  }

  // EntryVariantText
  .entry--text div p {
    &:last-child {
      margin-bottom: 0;
    }
  }

  // EntryVariantImage
  .entry figure figcaption p {
    margin: 7px 0 15px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .entry {
    word-wrap: break-word;
    padding: 12px 0;
  }

  a.external-link {
    &:after {
      // generate background image: https://codepen.io/koca/pen/aGgrEB
      background-image: url("data:image/svg+xml,%3csvg stroke='%234183c4' width='16' height='16' fill='none' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3e%3cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'%3e%3c/path%3e%3c/svg%3e");
      content: '';
      display: inline-block;
      margin-left: 3px;
      position: relative;
      top: 2px;
      width: 16px;
      height: 16px;
    }
  }
}

// QUIZ
.entry[data-question-result] {
  display: none;
}
section.remaining-questions {
  text-align: center;
  color: #f55656;
  line-height: 30px;
  font-size: 18px;
  margin-bottom: 15px;
  span {
    margin-right: 5px;
    &.sonuc {
      color: #424242;
      font-weight: bold;
    }
    &.remaining {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      margin: 0 5px;
      > span {
        margin: 0 4px;
      }
      .number {
        width: 23px;
        height: 23px;
        background-color: #424242;
        color: #fffafa;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
}
</style>
